import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

export interface AdminPlan {
  _id?: string;
  /** Some backend responses serialize the Mongo id as `id` instead of `_id`.
   *  We normalize to `_id` on read so callers always have a stable identifier. */
  id?: string;
  name: string;
  price: number;
  period: "day" | "week" | "month" | "year";
  features?: string[];
  dataLimit?: number | null;
  dataLimitUnit?: string;
  hasUnlimitedAccess?: boolean;
  isActive?: boolean;
  displayOrder?: number;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  // T-04 fields
  price_minor?: number;
  currency?: string;
  country_code?: string;
  duration_days?: number;
  device_limit?: number;
  data_cap_mb?: number | null;
  fair_use_mb_day?: number | null;
  rate_down_kbps?: number | null;
  rate_up_kbps?: number | null;
  rate_after_fup_kbps?: number | null;
  tax_profile_id?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface AdminPlanResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: AdminPlan | AdminPlan[];
}

/** Normalize a plan so `_id` is always set (backend may send `id` instead). */
const normalizePlan = (p: AdminPlan | null | undefined): AdminPlan | null => {
  if (!p) return null;
  return { ...p, _id: p._id ?? p.id };
};

// List ALL plans (active + inactive)
export const getAdminPlans = async (): Promise<AdminPlan[]> => {
  const res = await apiClient.get<AdminPlanResponse>(ENDPOINTS.ADMIN.PLANS);
  const d = res.data?.data;
  return Array.isArray(d) ? d.map((p) => normalizePlan(p) as AdminPlan) : [];
};

export const getAdminPlan = async (id: string): Promise<AdminPlan | null> => {
  const res = await apiClient.get<AdminPlanResponse>(ENDPOINTS.ADMIN.PLAN_DETAIL(id));
  const d = res.data?.data;
  return normalizePlan(d && !Array.isArray(d) ? d : null);
};

export const createAdminPlan = async (payload: Partial<AdminPlan>): Promise<AdminPlan | null> => {
  const res = await apiClient.post<AdminPlanResponse>(ENDPOINTS.ADMIN.PLAN_CREATE, payload);
  const d = res.data?.data;
  return normalizePlan(d && !Array.isArray(d) ? d : null);
};

// Update (edit) a plan. Contract: PUT /admin/plans/:id (per-id path) with the
// updated fields in the body. :id is the plan's MongoDB _id.
export const updateAdminPlan = async (id: string, payload: Partial<AdminPlan>): Promise<AdminPlan | null> => {
  const res = await apiClient.put<AdminPlanResponse>(ENDPOINTS.ADMIN.PLAN_UPDATE(id), payload);
  const d = res.data?.data;
  return normalizePlan(d && !Array.isArray(d) ? d : null);
};

/**
 * Activate / deactivate a plan.
 * Contract: PATCH /admin/plans/:id/toggle with an empty body. :id is the plan's
 * MongoDB _id. The backend flips isActive and returns the updated plan.
 */
export const toggleAdminPlan = async (id: string): Promise<AdminPlan | null> => {
  const res = await apiClient.patch<AdminPlanResponse>(ENDPOINTS.ADMIN.PLAN_TOGGLE(id), {});
  const d = res.data?.data;
  return normalizePlan(d && !Array.isArray(d) ? d : null);
};

/**
 * Delete a plan. Treats any 2xx as success — a DELETE commonly returns 204 (no
 * body) or a body without a `success` flag, so keying off `res.data.success`
 * alone wrongly reports failure on a successful delete.
 */
export const deleteAdminPlan = async (id: string): Promise<boolean> => {
  const res = await apiClient.delete<AdminPlanResponse>(ENDPOINTS.ADMIN.PLAN_DELETE(id));
  if (res.status >= 200 && res.status < 300) {
    // Respect an explicit success:false if the backend sends one.
    return res.data?.success !== false;
  }
  return false;
};
